import { useEffect, useState } from "react";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useForm } from "@/hooks/use-form";
import { useFormBuilderStore } from "@/stores/form-builder-store";
import { FormHeader } from "@/components/forms/FormHeader";
import { FieldList } from "@/components/forms/FieldList";
import { AssignmentSection } from "@/components/forms/AssignmentSection";
import { FormPreview } from "@/components/forms/FormPreview";

interface FormEditorProps {
  formId: string;
}

export function FormEditor({ formId }: FormEditorProps) {
  const { form, loading } = useForm(formId);
  const setForm = useFormBuilderStore((s) => s.setForm);
  const clearForm = useFormBuilderStore((s) => s.clearForm);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (form) setForm(form);
    return () => clearForm();
  }, [form, setForm, clearForm]);

  if (loading || !form) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading form...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader form={form} />

      {/* Mobile preview toggle */}
      <div className="md:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? (
            <><EyeOff className="mr-2 h-4 w-4" />Editor</>
          ) : (
            <><Eye className="mr-2 h-4 w-4" />Preview</>
          )}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Left: Editor */}
        <div className={`flex-1 space-y-6 ${showPreview ? "hidden md:block" : ""}`}>
          <FieldList form={form} />
          {form.type === "standard" && (
            <AssignmentSection formId={form.id} />
          )}
        </div>

        {/* Right: Preview */}
        <div
          className={`md:w-[400px] md:block ${showPreview ? "" : "hidden"}`}
        >
          <div className="sticky top-6 rounded-lg border p-6">
            <h3 className="mb-4 text-sm font-medium text-muted-foreground">
              Preview
            </h3>
            <FormPreview form={form} />
          </div>
        </div>
      </div>
    </div>
  );
}
